/**
 * Data Privacy Mode for the Choice dashboard. The Choice thunks mask what the API returns and
 * turn masked school filters back into real ones before calling it, so every Choice component
 * (tables, drawer, waitlist, charts, dropdowns, URL) only ever sees fakes while the mode is on.
 */
import { ChoiceDashboardFilters } from "../../domain/entities/ChoiceDashboard";
import { isDataPrivacyOn } from "./dataPrivacy";
import { maskDob, maskFirstName, maskId, maskLastName, maskReason, maskSchool, unmaskSchool } from "./dataPrivacyMasker";

type Row = Record<string, any>;

const OTHER_ORIGINS = "Other schools";

export const choicePrivacyOn = (): boolean => isDataPrivacyOn();

/** School back to the real name; the search is dropped (it would have to match real names). */
export const unmaskChoiceFilters = <T extends Partial<ChoiceDashboardFilters> | null | undefined>(f: T): T => {
  if (!f || !isDataPrivacyOn()) return f;
  return { ...f, school: f.school ? unmaskSchool(f.school) || "" : f.school, search: "" } as T;
};

export const maskChoiceRow = (r: Row): Row => {
  const c: Row = { ...r };
  if ("student_dob" in r) c.student_dob = maskDob(r.student_last_name, r.student_first_name, r.student_dob);
  c.student_last_name = maskLastName(r.student_last_name);
  c.student_first_name = maskFirstName(r.student_first_name);
  for (const [k, kind] of [["student_id", "student"], ["order_id", "order"], ["preference_group_id", "group"], ["parent_account_id", "parent"], ["school_number", "school-number"]] as const) {
    if (k in r) c[k] = maskId(kind, r[k]);
  }
  for (const k of ["school_name", "zoned_school", "last_school_attended"]) if (k in r) c[k] = maskSchool(r[k]);
  if ("seat_removed_reason" in r) c.seat_removed_reason = maskReason(r.seat_removed_reason);
  return c;
};

const maskAggregate = (r: Row): Row => ({
  ...r,
  ...(r.school_name !== undefined ? { school_name: maskSchool(r.school_name) } : {}),
  ...(r.school_number !== undefined ? { school_number: maskId("school-number", r.school_number) } : {}),
});

export const maskChoicePayload = <T extends Row | null | undefined>(p: T): T => {
  if (!p || !isDataPrivacyOn()) return p;
  const out: Row = { ...p, importSource: null };
  if (p.lastRun?.message) out.lastRun = { ...p.lastRun, message: p.lastRun.state === "FAILED" ? "Import failed (details hidden in Data Privacy Mode)" : null };
  if (Array.isArray(p.summary)) out.summary = p.summary.map(maskAggregate);
  if (Array.isArray(p.capacity)) out.capacity = p.capacity.map(maskAggregate);
  if (p.origins) {
    out.origins = Object.fromEntries(
      Object.entries(p.origins).map(([k, rows]) => [
        k,
        Array.isArray(rows) ? rows.map((r: Row) => (r.origin && r.origin !== OTHER_ORIGINS ? { ...r, origin: maskSchool(r.origin) } : r)) : rows,
      ])
    );
  }
  if (p.options?.schools) {
    out.options = { ...p.options, schools: (p.options.schools as string[]).map((s) => maskSchool(s) as string).sort((a, b) => a.localeCompare(b)) };
  }
  return out as T;
};

export const maskChoiceRowsPage = <T extends Row | null | undefined>(page: T): T => {
  if (!page || !isDataPrivacyOn() || !Array.isArray(page.rows)) return page;
  return { ...page, rows: page.rows.map(maskChoiceRow) } as T;
};

// ── CSV ──────────────────────────────────────────────────────────────

/** RFC 4180 parse: quoted fields, doubled quotes, commas and newlines inside quotes. */
export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

const csvCell = (v: unknown): string => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The server's applications CSV with every identifying column masked, header unchanged. */
export const maskChoiceCsv = (csv: string): string => {
  if (!isDataPrivacyOn() || !csv) return csv;
  const [header, ...rows] = parseCsv(csv);
  if (!header) return csv;
  const masked = rows
    .filter((r) => r.length > 1 || r[0] !== "")
    .map((cells) => {
      const obj: Row = {};
      header.forEach((h, i) => (obj[h] = cells[i] ?? ""));
      const m = maskChoiceRow(obj);
      return header.map((h) => csvCell(m[h])).join(",");
    });
  return [header.map(csvCell).join(","), ...masked].join("\n") + "\n";
};
